import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import aiAutomationApi from '../../utils/aiAutomationApi';
import { useSettings } from '../../contexts/SettingsContext';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * ImportCandidatesModal — bulk-import resume-less candidates for ONE job via Excel.
 *
 * Flow: download template → pick file → upload → summary. On success, onImported() lets the
 * workspace refresh so the new candidates appear in the READY bucket.
 */
const ImportCandidatesModal = ({ open, onClose, sessionId, searchId, companyId, onImported }) => {
  const { settings } = useSettings();
  const { isDark } = useTheme();
  const primary = settings?.theme?.colors?.primary || '#4f46e5';

  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  if (!open) return null;

  const reset = () => {
    setFile(null); setResult(null); setError(''); setBusy(false); setDragOver(false);
    if (inputRef.current) inputRef.current.value = '';
  };
  const close = () => { reset(); onClose?.(); };

  // ── file selection (shared by the picker + drag-and-drop) ──
  const ACCEPT = ['.xlsx', '.xls', '.csv'];
  const isAllowed = (f) => !!f && ACCEPT.some((ext) => f.name.toLowerCase().endsWith(ext));
  const formatBytes = (n) => {
    if (n == null) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };
  const acceptFile = (f) => {
    if (!f) return;
    if (!isAllowed(f)) { setError('Please choose an .xlsx, .xls, or .csv file.'); return; }
    setError(''); setFile(f);
  };
  const openPicker = () => inputRef.current?.click();
  const removeFile = () => {
    setFile(null); setError('');
    if (inputRef.current) inputRef.current.value = '';   // so re-picking the SAME file still fires onChange
  };
  const onDrop = (e) => { e.preventDefault(); setDragOver(false); acceptFile(e.dataTransfer?.files?.[0] || null); };
  const onDragOver = (e) => { e.preventDefault(); if (!dragOver) setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); setDragOver(false); };

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    setError('');
    try {
      const blob = await aiAutomationApi.downloadBlob('/interviews/imported-candidates/template');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'imported_candidates_template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('Could not download the template. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const downloadErrors = () => {
    const cols = [
      ['Full Name', 'name'], ['Email', 'email'], ['Phone', 'phone'],
      ['Current Role/Title', 'current_role'], ['Total Experience (years)', 'total_experience'],
      ['Key Skills (comma-separated)', 'skills'], ['Location', 'location'],
      ['Highest Education', 'highest_education'], ['Notes', 'notes'],
    ];
    const esc = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = [...cols.map((c) => c[0]), 'Error'].join(',');
    const lines = (result?.errors || []).map((e) => {
      const d = e.data || {};
      const vals = cols.map(([, key]) => esc(d[key] ?? (key === 'email' ? e.email : '')));
      vals.push(esc(e.reason || ''));
      return vals.join(',');
    });
    const blob = new Blob([[header, ...lines].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'imported_candidates_errors.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleUpload = async () => {
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('session_id', sessionId || '');
      if (searchId) fd.append('search_id', String(searchId));
      if (companyId) fd.append('company_id', String(companyId));
      const res = await aiAutomationApi.postForm('/interviews/imported-candidates/upload', fd);
      setResult(res);
      if ((res?.created || 0) > 0) onImported?.();
    } catch (e) {
      setError(e?.message || 'Upload failed. Please check the file and try again.');
    } finally {
      setBusy(false);
    }
  };

  const panelBg = isDark ? '#0f172a' : '#ffffff';

  return createPortal(
    <div
      className="fixed inset-0 z-[135] flex items-center justify-center bg-gray-950/60 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={close}
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-2xl shadow-2xl"
        style={{ backgroundColor: panelBg }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4" style={{ backgroundColor: `${primary}0f` }}>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Import candidates</h2>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Upload an Excel of candidates for this job.</p>
          </div>
          <button type="button" onClick={close} aria-label="Close"
            className="shrink-0 rounded-lg p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" /></svg>
          </button>
        </div>

        <div className="px-5 py-4">
          {/* Scope notice — the key rule for imported candidates */}
          <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/15 dark:text-amber-300">
            <svg viewBox="0 0 24 24" className="mt-0.5 h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" strokeLinecap="round" /></svg>
            <span>These candidates belong to <b>this job only</b> — no resume is stored, the AI interview is JD-based, and they never enter the shared candidate pool or any other job.</span>
          </div>

          {result ? (
            // ── Result summary ──
            <div>
              <div className="grid grid-cols-2 gap-2 text-center">
                {[
                  { label: 'Added', value: result.created || 0, color: '#10b981' },
                  { label: 'Skipped', value: result.skipped || 0, color: (result.skipped ? '#ef4444' : '#9ca3af') },
                ].map((tile) => (
                  <div key={tile.label} className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 dark:border-gray-700 dark:bg-gray-800/60">
                    <div className="text-2xl font-bold" style={{ color: tile.color }}>{tile.value}</div>
                    <div className="mt-0.5 text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{tile.label}</div>
                  </div>
                ))}
              </div>
              {Array.isArray(result.errors) && result.errors.length > 0 && (
                <div className="mt-3">
                  <div className="mb-1.5 flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">{result.errors.length} row(s) skipped</span>
                    <button type="button" onClick={downloadErrors}
                      className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: primary }}>
                      <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3v12m0 0 4-4m-4 4-4-4M4 21h16" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      Download error rows
                    </button>
                  </div>
                  <div className="max-h-40 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700">
                    {result.errors.map((err, i) => {
                      const who = err.name || err.data?.name;
                      return (
                        <div key={i} className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-1.5 text-xs last:border-0 dark:border-gray-800">
                          <span className="truncate text-gray-600 dark:text-gray-300">
                            {who ? <span className="font-medium">{who}</span> : `Row ${err.row}`}
                            {err.email ? ` · ${err.email}` : ''}
                          </span>
                          <span className="shrink-0 text-red-500">{err.reason}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="mt-4 flex items-center justify-end gap-2">
                <button type="button" onClick={reset}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
                  Import more
                </button>
                <button type="button" onClick={close}
                  className="rounded-lg px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition"
                  style={{ backgroundColor: primary }}>
                  Done
                </button>
              </div>
            </div>
          ) : (
            // ── Upload form ──
            <div>
              <div className="mb-3 flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300">1. Get the template</span>
                <button type="button" onClick={handleDownloadTemplate} disabled={downloading}
                  className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition disabled:opacity-50"
                  style={{ borderColor: `${primary}55`, color: primary }}>
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3v12m0 0 4-4m-4 4-4-4M4 21h16" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  {downloading ? 'Preparing…' : 'Download template'}
                </button>
              </div>

              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">2. Upload the filled file</span>

              {/* One hidden input, driven by the dropzone AND the "Change" button (via the ref). */}
              <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
                onChange={(e) => acceptFile(e.target.files?.[0] || null)} />

              {!file ? (
                // ── Empty state: click OR drag & drop ──
                <button type="button" onClick={openPicker}
                  onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
                  className="mt-2 flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-6 text-center transition"
                  style={{
                    borderColor: dragOver ? primary : (isDark ? '#374151' : '#d1d5db'),
                    backgroundColor: dragOver ? `${primary}0d` : 'transparent',
                  }}>
                  <svg viewBox="0 0 24 24" className="h-7 w-7 text-gray-400" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" /><path d="M12 11v6m-3-3 3 3 3-3" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  <span className="mt-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                    {dragOver ? 'Drop the file to attach' : 'Click to choose, or drag & drop'}
                  </span>
                  <span className="mt-0.5 text-[11px] text-gray-400">.xlsx, .xls or .csv · Full Name and Email are required.</span>
                </button>
              ) : (
                // ── Selected state: a clear file card with size, change & remove ──
                <div className="mt-2 flex items-center gap-3 rounded-xl border px-3 py-2.5"
                  style={{ borderColor: `${primary}55`, backgroundColor: `${primary}0a` }}>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                    style={{ backgroundColor: `${primary}1f`, color: primary }}>
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" /><path d="M8 13h8M8 17h5" strokeLinecap="round" /></svg>
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-medium text-gray-800 dark:text-gray-100" title={file.name}>{file.name}</span>
                      <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 text-emerald-500" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="m5 13 4 4L19 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-[11px] text-gray-500 dark:text-gray-400">
                      <span>{formatBytes(file.size)}</span>
                      <span aria-hidden>·</span>
                      <button type="button" onClick={openPicker} className="font-semibold hover:underline" style={{ color: primary }}>Change file</button>
                    </div>
                  </div>
                  <button type="button" onClick={removeFile} aria-label="Remove selected file"
                    className="shrink-0 rounded-lg p-1.5 text-gray-400 transition hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20">
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" /></svg>
                  </button>
                </div>
              )}

              {error && <p className="mt-3 text-xs font-medium text-red-500">{error}</p>}

              <div className="mt-4 flex items-center justify-end gap-2">
                <button type="button" onClick={close}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
                  Cancel
                </button>
                <button type="button" onClick={handleUpload} disabled={!file || busy}
                  className="rounded-lg px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-45"
                  style={{ backgroundColor: primary }}>
                  {busy ? 'Uploading…' : 'Upload candidates'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ImportCandidatesModal;
